<template>
  <div>
    <!-- request vacation button with modal -->
    <button
      type="button"
      class="btn btn-primary"
      @click="showRequestModal"
    >
      Request Leave
    </button>

    <!-- vacation request modal -->
    <div
      id="vacationRequestModal"
      class="modal fade"
      tabindex="-1"
      aria-labelledby="vacationRequestModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h1
              id="vacationRequestModalLabel"
              class="modal-title fs-5"
            >
              Leave Request
            </h1>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            />
          </div>
          <ValidationObserver v-slot="{ invalid }">
            <form
              ref="vacationForm"
              @submit.prevent="submitVacation"
            >
              <div class="modal-body">
                <!-- vacation date range select-->
                <div class="mb-3">
                  <vc-date-picker
                    v-model="vacationDateRange"
                    is-range
                    :masks="masks"
                  >
                    <template #default="{ inputValue, inputEvents }">
                      <div class="flex justify-center items-center">
                        <input
                          :value="inputValue.start"
                          class="border px-1 pb-1 w-32 rounded focus:outline-none focus:border-indigo-300"
                          name="start_date"
                          v-on="inputEvents.start"
                        >
                        -
                        <input
                          :value="inputValue.end"
                          class="border px-1 pb-1 w-32 rounded focus:outline-none focus:border-indigo-300"
                          name="end_date"
                          v-on="inputEvents.end"
                        >
                        <div
                          v-if="updatingRequest"
                          class="spinner-grow text-primary"
                          role="status"
                        >
                          <span class="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    </template>
                  </vc-date-picker>
                </div>

                <!-- vacation reason textarea -->
                <div class="mb-3">
                  <label
                    for="vacationReason"
                    class="form-label"
                  >
                    Reason
                  </label>
                  <ValidationProvider
                    v-slot="v"
                    name="Reason"
                    rules="required"
                  >
                    <textarea
                      id="vacationReason"
                      v-model="vacationReason"
                      name="reason"
                      class="form-control"
                      rows="3"
                      required
                    />
                    <span class="text-danger">{{ v.errors[0] }}</span>
                  </validationprovider>
                </div>

                <!-- sick leave checkbox -->
                <div class="mb-3">
                  <div class="form-check">
                    <input
                      id="sickLeaveCheck"
                      v-model="sickLeave"
                      type="checkbox"
                      name="sick_leave"
                      class="form-check-input"
                    >
                    <label
                      for="sickLeaveCheck"
                      class="form-check-label"
                    >Sick Leave</label>
                  </div>
                </div>

                <!-- half day checkbox -->
                <div
                  v-if="dateCount === 1"
                  class="mb-3"
                >
                  <div class="form-check">
                    <input
                      id="halfDayCheck"
                      v-model="halfDay"
                      type="checkbox"
                      name="half_day"
                      class="form-check-input"
                    >
                    <label
                      for="halfDayCheck"
                      class="form-check-label"
                    >Half Day Off</label>
                  </div>
                </div>
              </div>
              <div class="modal-footer">
                <button
                  type="button"
                  class="btn btn-secondary"
                  data-bs-dismiss="modal"
                >
                  Close
                </button>
                <button
                  type="submit"
                  class="btn btn-alt-primary"
                  :disabled="invalid"
                  @click="updateRequestType('save-draft')"
                >
                  Save draft
                </button>
                <button
                  type="submit"
                  class="btn btn-primary"
                  :disabled="invalid"
                  @click="updateRequestType('submit')"
                >
                  Submit
                </button>
              </div>
            </form>
          </ValidationObserver>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from "bootstrap";
import moment from "moment-timezone";
import { extend, ValidationObserver, ValidationProvider } from "vee-validate";
import { required } from "vee-validate/dist/rules";
import { mapActions, mapGetters } from "vuex";

extend("required", required);

export default {
    name: "StaffVacationRequestButton",
    components: {
        ValidationObserver,
        ValidationProvider,
    },
    props: {
        staffId: {
            type: Number,
            required: true,
        },
        year: {
            type: Number,
            required: false,
            default: () => moment().year(),
        },
        leaveType: {
            type: String,
            required: false,
            default: "vacation",
        },
    },
    data() {
        return {
            updatingRequest: false,
            vacationDateRange: {
                start: moment().format("YYYY-MM-DD"),
                end: moment().add(1, "month").format("YYYY-MM-DD"),
            },
            sickLeave: false,
            halfDay: false,
            masks: {
                input: "YYYY-MM-DD",
            },
            vacationReason: null,
            requestType: "save-draft",
        };
    },
    computed: {
        requestModal() {
            return new Modal(document.getElementById("vacationRequestModal"));
        },
        dateCount() {
            const start = moment(this.vacationDateRange.start).startOf("date");
            const end = moment(this.vacationDateRange.end).startOf("date");
            return end.diff(start, "days") + 1;
        },
        ...mapGetters({
            vacationList: "vacation/vacationList",
        }),
    },
    mounted() {
        if (this.leaveType === "sick") {
            this.sickLeave = true;
        }
    },
    methods: {
        async submitVacation() {
            this.updatingRequest = true;

            try {
                const formData = new FormData(this.$refs.vacationForm);
                formData.append("staff_id", this.staffId.toString());
                formData.append("request_type", this.requestType);

                await this.sendVacationRequest({ "formData": formData, "staffId": this.staffId });
                await this.getStaffVacations({ staffId: this.staffId });

                await this.$refs.vacationForm.reset();
                this.$emit("update-table");
                this.requestModal.hide();

                alert("Leave request sent successfully");
            } catch (e) {
                alert("Something went wrong, please try again later");
                console.log(e);
            } finally {
                this.updatingRequest = false;
            }
        },
        updateRequestType(type) {
            this.requestType = type;
        },
        showRequestModal() {
            this.requestModal.show();
        },
        ...mapActions({
            getStaffVacations: "vacation/getStaffVacations",
            sendVacationRequest: "vacation/sendVacationRequest",
        }),
    },
};
</script>

<style scoped>

</style>
